import React, { useCallback, useMemo, useState } from 'react'
import { FloatingTooltip, getTooltipPosition } from './index'

export interface UseTooltipOptions {
  maxWidth?: number
  maxHeight?: number
  rowHeight?: number
  padding?: number
  verticalGap?: number
}

export const useTooltip = (options: UseTooltipOptions = {}) => {
  const {
    maxWidth = 360,
    maxHeight = 280,
    rowHeight = 22,
    padding = 16,
    verticalGap = 4,
  } = options

  const [visible, setVisible] = useState(false)
  const [position, setPosition] = useState({ x: 0, y: 0 })
  const [content, setContent] = useState('')

  const handleMouseLeave = useCallback(() => {
    setVisible(false)
  }, [])

  const handleMouseEnter = useCallback(
    (e: React.MouseEvent<any>, text: string) => {
      const target = e.currentTarget as HTMLElement
      const targetRect = target?.getBoundingClientRect()
      const pos = getTooltipPosition(
        e.clientX,
        e.clientY,
        text,
        maxWidth,
        rowHeight,
        padding,
        targetRect,
        maxHeight,
        verticalGap,
      )

      setContent(text)
      setPosition(pos)
      setVisible(true)
    },
    [maxWidth, maxHeight, rowHeight, padding, verticalGap],
  )

  const createTableTooltipHandler = <T,>(getText: (record: T) => string) => {
    return (e: React.MouseEvent<HTMLElement>, record: T) => {
      const text = getText(record)
      if (!text) {
        setVisible(false)
        return
      }
      handleMouseEnter(e, text)
    }
  }

  const tooltipElement = useMemo(
    () => (
      <FloatingTooltip visible={visible} x={position.x} y={position.y}>
        {content}
      </FloatingTooltip>
    ),
    [visible, position.x, position.y, content],
  )

  return {
    tooltipElement,
    handleMouseEnter,
    handleMouseLeave,
    createTableTooltipHandler,
  }
}
